import { FlexCol, FlexRow } from '@/components/common/Flex';
import Icon from '@/components/common/icons/Icon';
import { Text } from '@/components/common/Typography';
import { iconByShareStatus } from '@/components/goals/helpers';
import { ButtonProps, MenuItem, Spinner } from '@chakra-ui/react';
import { ShareStatus } from '@common/graphql/generated';
import { IconName } from '@common/react/icons';
import { toTitleCase } from '@common/util/strings';
import { FC } from 'react';
import ActionMenu from './ActionMenu';
import { Button, MenuButton } from './Button';
import { ChevronDown } from './icons';

export interface ShareStatusMenuButtonProps {
    shareStatus: ShareStatus;
    onChangeShareStatus: (shareStatus: ShareStatus) => void;
    isLoading?: boolean;
    type: 'account' | 'goal';
    menuButtonProps?: ButtonProps;
}

const goalDescriptions: Record<ShareStatus, string> = {
    PRIVATE: `It’s just you investing, and your partner will never see anything.`,
    SHARED: `You’re both investing towards this goal, and can see each others' progress and activity.`,
};

const accountDescriptions: Record<ShareStatus, string> = {
    PRIVATE: `This account will NOT be visible to your partner at all. Think of it as Incognito mode for your account.`,
    SHARED: `This account is shared with your partner, and you consider the balance to be both of yours. It will only add to your combined net worth.`,
};

const ShareStatusActionMenu = ({
    shareStatus,
    onChangeShareStatus,
    isLoading,
    type,
    menuButtonProps,
}: ShareStatusMenuButtonProps) => {
    const descriptions =
        type === 'account' ? accountDescriptions : goalDescriptions;

    const shareStatuses = ['SHARED', 'PRIVATE'] as const;

    return (
        <ActionMenu
            menuButton={
                isLoading ? (
                    <Spinner size="sm" color="gray.500" />
                ) : (
                    <ShareStatusMenuButton
                        shareStatus={shareStatus}
                        menuButtonProps={menuButtonProps}
                    />
                )
            }
            menuListProps={{ maxWidth: '320px' }}
        >
            {shareStatuses.map((s) => (
                <ShareStatusMenuItem
                    key={s}
                    title={toTitleCase(s)}
                    description={descriptions[s]}
                    iconName={iconByShareStatus[s]}
                    onClick={() => {
                        if (s !== shareStatus) {
                            onChangeShareStatus(s);
                        }
                    }}
                />
            ))}
        </ActionMenu>
    );
};

const ShareStatusMenuButton: FC<{
    shareStatus: ShareStatus;
    menuButtonProps?: ButtonProps;
}> = ({ shareStatus, menuButtonProps }) => (
    <MenuButton
        as={Button}
        variant="outline"
        bg="transparent"
        size="sm"
        w={48}
        px={3}
        textAlign="left"
        rightIcon={<ChevronDown />}
        color="gray.600"
        fontSize="md"
        onClickEvent="Share Status Menu Button Clicked"
        {...menuButtonProps}
    >
        <Icon name={iconByShareStatus[shareStatus]} mr={3} />
        {toTitleCase(shareStatus)}
    </MenuButton>
);

const ShareStatusMenuItem: FC<{
    title: string;
    description: string;
    iconName: IconName;
    onClick: VoidFunction;
}> = ({ title, description, iconName, onClick }) => (
    <MenuItem onClick={onClick}>
        <FlexRow gap={3} py={4}>
            <FlexRow w={5} mt="3px">
                <Icon name={iconName} size="sm" />
            </FlexRow>
            <FlexCol gap={2}>
                <Text fontSize="md" fontWeight="medium">
                    {title}
                </Text>
                <Text fontSize="sm">{description}</Text>
            </FlexCol>
        </FlexRow>
    </MenuItem>
);

export default ShareStatusActionMenu;
